import {
  ICreateReturnDestinationAddress,
  ICreateReturnItem,
  ICreateReturnBoxCount,
  IItemListCreateReturnRequest,
  IUpsGroupingRlcReturn,
  PolicyValidatorAddress,
} from '@src/models/api';
import { IBuildSsReturnCreate } from '@src/models/finalize-return';
import { ItemToReturn } from '@src/models/returns';
import { IReturnShipments } from '@src/stores/returnStore';
import {
  RETURN_METHOD,
  RETURN_METHOD_TO_SHIPMENT_MAP,
  isClientSideOnlyMethod,
} from '@src/constants/general';
import { AdaptedReturnMethods } from './eligibility-utils';
import {
  getLORPShipFromAddress,
  getOrderDetailsReturnCreateFields,
  getSsReturnCreateShipFromAddress,
} from '.';

export interface IBuildItemListCreateReturn extends IBuildSsReturnCreate {
  /** The service's per-item answer, for the return address and its ids. */
  adaptedMethods: AdaptedReturnMethods;
  /** The shipping service's groups, which name the suite each count belongs to. */
  upsGrouping?: IUpsGroupingRlcReturn[];
}

/**
 * The customer's chosen box count, keyed by the suite it belongs to.
 *
 * The count is read off the leg rather than off the group, because the group
 * only states the floor: the picker seeds from it and the customer may raise
 * it, and the raised number is what has to reach the service. Reading the
 * group back would silently send the floor and discard the choice.
 *
 * The leg is found by the suite it was built from, not by a shared line.
 * Sharing a line only identifies the right leg while legs and groups are
 * one-to-one, which holds when the shipping service has just built them and
 * stops holding after a review-step edit: that re-runs the validator, which
 * rebuilds the drop-off legs on its own address split while these groups still
 * describe the service's. Two groups then match the one leg that replaced
 * them, and both take its count — so a customer who chose five boxes is
 * issued eight.
 *
 * Identity holds only while the suite is intrinsic to the leg, so the
 * validator's rebuild clears it. An inherited suite misattributes in the other
 * direction — one group claiming a leg it no longer describes, its count sent
 * for lines it never grouped while the other group's count goes unsent and
 * that group's items travel anyway. Cleared, no group finds a leg, and the
 * caller omits the map rather than sending part of it: a rebuild clears every
 * drop-off leg at once, so the honest post-edit state is no count at all
 * rather than a subset. The floor is dropped in the same place for the same
 * reason.
 *
 * That honesty covers the request and not the screen. The count the customer
 * chose stays on the leg, and the review card and its box drawer both go on
 * showing it, so what they see and what is sent disagree until the shipping
 * service is asked again — which is separate work.
 *
 * A group with no leg is therefore the normal post-edit state as well as the
 * removal case, and both are dropped rather than sent as a bare suite — an
 * entry naming a suite with no count behind it is undefined on the contract.
 */
const toBoxCounts = (
  upsGrouping: IUpsGroupingRlcReturn[],
  returnShipments?: IReturnShipments[],
): ICreateReturnBoxCount[] =>
  upsGrouping.flatMap((group) => {
    // Drop-off legs only, defensively: a count belongs to a shipping group and
    // the groups are drop-off's alone. No path puts a suite on a pickup leg —
    // only the grouping answer sets one, on drop-off legs, and the validator's
    // rebuild now clears it — so this narrows a state the flow cannot reach
    // and is kept for the invariant rather than for a live case.
    const leg = returnShipments?.find(
      (shipment) =>
        shipment.shipmentType === 'DROPOFF' &&
        shipment.rlcReturnSuite === group.rlcReturnSuite,
    );
    if (!leg) return [];
    return [
      {
        returnSuiteId: group.rlcReturnSuite,
        noOfBoxes: leg.boxQuantity ?? group.boxQuantity,
      },
    ];
  });

/**
 * The leg an item sits on, used only for the values the customer entered on it
 * — pickup date and text opt-in. Everything describing the item itself comes
 * from the item.
 */
const findLegFor = (
  item: ItemToReturn,
  returnShipments?: IReturnShipments[],
): IReturnShipments | undefined =>
  returnShipments?.find((shipment) =>
    shipment.lineItems.some((line) => line.lineItemId === item.lineItemId),
  );

/**
 * Narrows a leg's return address to the shape the create call names.
 *
 * Takes the leg's address rather than re-reading the service's answer, because
 * the leg is the answer plus a guard: the methods contract marks the address
 * optional, so the composer falls back to the validator's when the service
 * gives none. Reading the answer again here would step around that guard and
 * send an empty object for exactly the case it was added for.
 *
 * `addressId` is left off: the methods contract has no such field, so there is
 * nothing to fill it from. `emailId` likewise — the destination is a return
 * centre, not the shopper, and inventing one from customer data would be
 * wrong rather than merely empty.
 */
const toDestinationAddress = (
  address?: PolicyValidatorAddress,
): ICreateReturnDestinationAddress => ({
  addressLine1: address?.addressLine1,
  addressLine2: address?.addressLine2,
  city: address?.city,
  state: address?.state,
  zipCode: address?.zipCode,
  country: address?.country,
  dayPhone: address?.dayPhone,
  contactName: address?.contactName,
});

/**
 * Builds the item-list form of the create request: a flat list of items each
 * carrying its own addresses and weight, rather than shipments carrying them
 * for a group.
 *
 * One call per return method, as today — `shipmentType` selects the items.
 * In-store and SORIS never take this shape: the first has no backend enum
 * value and the second submits through its own endpoint.
 *
 * `boxCounts` carries the customer's chosen box count keyed by return suite,
 * the one group-shaped thing left in an otherwise item-native request. Only
 * the shipping service's grouping answer names those suites, so the map is
 * sent when that answer is in hand and can still be tied to the legs the
 * customer set counts on, and omitted when it is not — including after a
 * review-step edit, which rebuilds those legs and leaves the answer stale.
 *
 * Pickup calls never carry it: the grouping call is made for the drop-off
 * lines alone, and pickup has no suites for a count to be keyed to.
 *
 * `pickupDate` and `textOptIn` are sent for any pickup call, where the
 * shipment shape gates both on the scheduled-pickup switch as well. The
 * divergence is deliberate: that switch has been on for a long time and is not
 * expected to be turned off again, and with it on the shipment shape's
 * condition reduces to the one used here. Retiring the gate on that side is
 * its own piece of work, since it reaches which flow steps render.
 */
export const buildItemListCreateReturnRequest = ({
  orderDetailsV2,
  isGuest,
  policyValidatorET,
  deliveryDictionary,
  returnShipments,
  selectedItems,
  adaptedMethods,
  upsGrouping,
  shipmentType,
  orderVersion,
  isKeepIt,
}: IBuildItemListCreateReturn): IItemListCreateReturnRequest => {
  const {
    orderId,
    emailId,
    billingAddress,
    locationId,
    orderDate,
    net = '',
  } = getOrderDetailsReturnCreateFields(orderDetailsV2);

  const itemsForMethod = selectedItems.filter(
    (item) =>
      item.returnMethod &&
      // Client-side methods by the shared predicate rather than by name, so
      // APO/FPO is covered: it maps to DROPOFF for typing and would otherwise
      // pass this filter. SORIS is named separately — it is not client-side,
      // it submits through an endpoint of its own.
      !isClientSideOnlyMethod(item.returnMethod) &&
      item.returnMethod !== RETURN_METHOD.SORIS &&
      RETURN_METHOD_TO_SHIPMENT_MAP[item.returnMethod] === shipmentType,
  );

  // One origin for the whole call, not one per line. The helper picks the
  // ship-to-home line out of the list it is given, so a list of one is just that
  // line — and a store-fulfilled line has no shipping address to find, so it
  // would send no origin at all while a sibling line on the same return sent a
  // real one. Asking once also keeps this agreeing with the grouping call, which
  // resolves the same address from the same lines; disagree and the service is
  // entitled to price the boxes differently at submit than it did at review.
  //
  // Taken from itemsForMethod rather than from the legs: this builder is entered
  // once per shipment type and every line it sends comes from that filter, so the
  // list is non-empty whenever there is anything to give an origin to. The legs
  // are optional here, which is the opposite of getFinalizeReturnShipments —
  // gathering from them would be the same blank, mirrored.
  const dropOffOrigin = getSsReturnCreateShipFromAddress(
    emailId,
    itemsForMethod,
    deliveryDictionary,
  );

  const lineItems = itemsForMethod.map((item): ICreateReturnItem => {
    const isLORP = item.returnMethod === RETURN_METHOD.LORP;
    const leg = findLegFor(item, returnShipments);
    return {
      lineItemId: item.lineItemId,
      returnQuantity: item.returnQuantity,
      returnReasonCode: item.returnReasonCode,
      // The item's own weight, not weight x quantity. See ICreateReturnItem.
      itemWeight: String(item.itemWeight ?? ''),
      // The pickup address is this item's own — it comes from what the customer
      // typed on that item's leg, and the fulfillment group is only consulted for
      // that line's phone numbers. The drop-off origin is the shared one above.
      senderAddress: isLORP
        ? getLORPShipFromAddress(
            leg?.lorpContactDetails || {},
            orderDetailsV2.fulfillmentGroups,
            [item],
          )
        : dropOffOrigin,
      destinationAddress: toDestinationAddress(leg?.shipToAddress),
      rtvItemId: adaptedMethods[item.lineItemId]?.rtvItemId || '',
      rtvOptionId: item.returnMethod
        ? adaptedMethods[item.lineItemId]?.methods[item.returnMethod]
            ?.optionId || ''
        : '',
      sku: String(item.sku ?? ''),
      poNumber: item.poNumber || '',
      productType: item.productType,
      // From the service's answer, not the item: the v2 eligibility adapter
      // leaves ItemToReturn.hazmatItem undefined under ROS, so reading it here
      // would call every item safe to ship by parcel.
      hazmatItem: adaptedMethods[item.lineItemId]?.hazmat ?? false,
      itemDescription: item.itemDescription || '',
      ...(isLORP ? { pickupDate: leg?.pickupDate ?? '' } : {}),
    };
  });

  const pickupLeg = returnShipments?.find(
    (shipment) => shipment.shipmentType === 'PICKUP',
  );

  // Sent only when it says something. Every group losing its leg is what a
  // review-step edit leaves behind, and an empty map would assert a count of
  // nothing for suites whose items are still on the request; an absent key
  // says the count is unknown, which is what is true.
  const boxCounts =
    shipmentType === 'DROPOFF' && upsGrouping?.length
      ? toBoxCounts(upsGrouping, returnShipments)
      : [];

  return {
    requestShape: 'ITEM_LIST',
    requestedShipmentType: shipmentType,
    orderId,
    et: policyValidatorET,
    billingAddress,
    emailId,
    isGuest,
    net,
    locationId: locationId.toString(),
    orderDate,
    orderVersion: orderVersion || '',
    isKeepIt: isKeepIt ?? false,
    // Order-level here, where the shipment shape carries it per leg.
    ...(shipmentType === 'PICKUP'
      ? { textOptIn: pickupLeg?.textOptIn ?? false }
      : {}),
    ...(boxCounts.length ? { boxCounts } : {}),
    lineItems,
  };
};
